import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { Modal } from "@mui/material";
import { AddAPhoto as AddAPhotoIcon } from "@mui/icons-material";
import CustomInput from "../shared/custom-input/custom-input";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { FormModalProps } from "./offerModal";
import { getUserGroupService } from "../services/group-service";
import CustomSelect from "../shared/custom-select/custom-select";
import { SnackbarContext } from "../config/hooks/use-toast";
import { classesLevel } from "../mocks/education-level";
import { getAllSubjectsByGroupId } from "../services/subject-service";

interface OfferFormData {
  image: string;
  title: string;
  subTitle: string;
  description: string;
  monthlyPrice: number | string;
  trimesterPrice: number | string;
  semesterPrice: number | string;
  yearlyPrice: number | string;
  offerDetails: string;
  classId: number | string;
  requiredEducationLevel?: string;
  allSubjects?: boolean;
  autoAssignSubjects?: boolean;
  subjectCount?: number | string;
  freeDurationDays?: number | string;
  subjectIds?: number[];
  // promoCode?: string;
}

const defaultData: OfferFormData = {
  image: "",
  title: "",
  subTitle: "",
  description: "",
  monthlyPrice: "",
  trimesterPrice: "",
  semesterPrice: "",
  yearlyPrice: "",
  offerDetails: "",
  classId: "",
  requiredEducationLevel: "",
  allSubjects: false,
  autoAssignSubjects: true,
  subjectCount: "",
  freeDurationDays: "",
  subjectIds: [],
  // promoCode: "",
};
  
const OfferStudentModal: React.FC<FormModalProps> = ({
  open,
  onClose,
  initialData = defaultData,
  modalTitle,
  buttonText,
  onButtonClick,
  onImageChange,
}) => {
  const snackbarContext = useContext(SnackbarContext);
  const [sendeData, setSendeData] = useState<OfferFormData>(initialData as OfferFormData);
  const [pricingMode, setPricingMode] = useState<"free" | "paid">("paid");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [benefits, setBenefits] = useState<string[]>([]);
  const [benefitInput, setBenefitInput] = useState<string>("");
  const [groupOptions, setGroupOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [availableSubjects, setAvailableSubjects] = useState<
    { id: number; speciality: string }[]
  >([]);

  const initialEducationLevel = classesLevel.find(
    (level) => level.value === (initialData as OfferFormData).requiredEducationLevel,
  );

  const withZeroPrices = (data: OfferFormData): OfferFormData => ({
    ...data,
    monthlyPrice: "0",
    trimesterPrice: "0",
    semesterPrice: "0",
    yearlyPrice: "0",
  });

  const handlePricingModeChange = (mode: "free" | "paid") => {
    setPricingMode(mode);
    setSendeData((prevData) => {
      if (mode === "free") {
        return withZeroPrices(prevData);
      }

      return {
        ...prevData,
        freeDurationDays: "",
      };
    });
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setSendeData((prevData) => {
      const nextData = { ...prevData, [name]: value } as OfferFormData;

      // When free duration is provided, auto-apply zero prices for all periods.
      if (pricingMode === "free" && name === "freeDurationDays" && value !== "") {
        return withZeroPrices(nextData);
      }

      return nextData;
    });
  };

  const loadGroupSubjects = (groupId: number | string, selectedIds: number[] = []) => {
    const numericGroupId = Number(groupId);
    if (!numericGroupId) {
      setAvailableSubjects([]);
      setSendeData((prevData) => ({ ...prevData, subjectIds: [] }));
      return;
    }

    getAllSubjectsByGroupId(numericGroupId)
      .then((res: any) => {
        const subjects = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setAvailableSubjects(subjects);
        const allowedIds = new Set(subjects.map((subject: any) => Number(subject.id)));
        setSendeData((prevData) => ({
          ...prevData,
          subjectIds: selectedIds.filter((id) => allowedIds.has(Number(id))),
        }));
      })
      .catch((error) => {
        console.log(error);
        setAvailableSubjects([]);
      });
  };

  const toggleSubject = (subjectId: number) => {
    setSendeData((prevData) => {
      const currentSubjectIds = prevData.subjectIds || [];
      const nextSubjectIds = currentSubjectIds.includes(subjectId)
        ? currentSubjectIds.filter((id) => id !== subjectId)
        : [...currentSubjectIds, subjectId];

      return {
        ...prevData,
        subjectIds: nextSubjectIds,
        subjectCount: nextSubjectIds.length > 0 ? nextSubjectIds.length : prevData.subjectCount,
      };
    });
  };

  const handleImageUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setImageFile(file);
        if (onImageChange) {
          onImageChange(result);
        }
      };
      reader.readAsDataURL(file);
    } else {
      console.error("No file selected or file is invalid.");
    }
  };

  const handleAddBenefit = () => {
    if (benefits.length < 5) {
      if (benefitInput.trim() && benefitInput.length < 30) {
        setBenefits([...benefits, benefitInput.trim()]);
        setBenefitInput("");
      }
    }
  };

  const handleRemoveBenefit = (index: number) => {
    setBenefits(benefits.filter((_, i) => i !== index));
  };

  const handleActionClick = () => {
    const formData = new FormData();
    const normalizedData = pricingMode === "free"
      ? withZeroPrices(sendeData)
      : { ...sendeData, freeDurationDays: "" };
    const payloadData: OfferFormData = {
      ...normalizedData,
      autoAssignSubjects:
        normalizedData.autoAssignSubjects === undefined
          ? true
          : normalizedData.autoAssignSubjects,
    };
    const { image, ...selectedUser } = payloadData;

    const offerDetails = benefits.join(" \n ");
    // Add promoCode to the payload if present
    formData.append(
      "studentOfferDTOJson",
      JSON.stringify({ ...selectedUser, offerDetails }),
    );
    if (imageFile) {
      formData.append("image", imageFile);
    }

    const isFree = pricingMode === "free";

    if (!isFree && (!imageFile && !normalizedData.image)) {
      if (snackbarContext) {
        snackbarContext.showMessage(
          "Erreur",
          "Veuiller importer une image",
          "error",
        );
      }
      return;
    }

    onButtonClick(formData);
  };

  useEffect(() => {
    if (open) {
      getUserGroupService()
        .then((res) => {
          const options = res.data.map((group: any) => ({
            label: group.title,
            value: group.id,
          }));
          setGroupOptions(options);
        })
        .catch((e) => {
          console.log(e);
        });
      const initialFormData = initialData as OfferFormData;
      const initialMode = [
        initialFormData.monthlyPrice,
        initialFormData.trimesterPrice,
        initialFormData.semesterPrice,
        initialFormData.yearlyPrice,
      ].every((price) => price === "" || price === undefined || price === null || Number(price) === 0)
        ? "free"
        : "paid";
      setPricingMode(initialMode);
      setSendeData(
        initialMode === "free"
          ? {
              ...initialFormData,
              monthlyPrice: "0",
              trimesterPrice: "0",
              semesterPrice: "0",
              yearlyPrice: "0",
              autoAssignSubjects:
                initialFormData.autoAssignSubjects === undefined
                  ? true
                  : initialFormData.autoAssignSubjects,
            }
          : {
              ...initialFormData,
              autoAssignSubjects:
                initialFormData.autoAssignSubjects === undefined
                  ? true
                  : initialFormData.autoAssignSubjects,
            },
      );
      setImageFile(null);
      setBenefits(
        initialData.offerDetails ? initialData.offerDetails.split("\n") : [],
      );
      const selectedIds = Array.isArray((initialData as OfferFormData).subjectIds)
        ? ((initialData as OfferFormData).subjectIds as number[])
        : [];
      if ((initialData as OfferFormData).classId) {
        loadGroupSubjects((initialData as OfferFormData).classId, selectedIds);
      } else {
        setAvailableSubjects([]);
      }
    }
  }, [open, initialData]);

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;
    setSendeData((prevData) => {
      const nextData = {
        ...prevData,
        [name]: value,
      };

      if (name === "classId") {
        nextData.subjectIds = [];
      }

      return nextData;
    });

    if (name === "classId") {
      loadGroupSubjects(value, []);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        setSendeData(defaultData);
        setPricingMode("paid");
        setImageFile(null);
        onClose();
      }}
      className="flex justify-end backdrop-blur-sm bg-title/20"
      closeAfterTransition
    >
      <div className={`h-full w-full max-w-[550px] bg-white shadow-[-20px_0_80px_rgba(0,0,0,0.1)] outline-none transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] flex flex-col ${open ? 'translate-x-0' : 'translate-x-full'}`}>
        
        {/* Drawer Header */}
        <div className="z-30 flex items-center justify-between flex-shrink-0 p-8 pb-6 border-b border-gray-50 bg-white/50 backdrop-blur-md">
          <div>
            <h2 className="text-2xl font-black tracking-tighter uppercase text-title">{modalTitle}</h2>
            <p className="text-[9px] font-black text-primary uppercase tracking-[0.4em] mt-1">Configuration de l'offre étudiant</p>
          </div>
          <button 
            onClick={() => { setSendeData(defaultData); setPricingMode("paid"); setImageFile(null); onClose(); }}
            className="flex items-center justify-center w-12 h-12 transition-all duration-300 cursor-pointer rounded-2xl bg-gray-50 text-text/30 hover:bg-rose-50 hover:text-rose-500 group"
          >
             <span className="text-2xl font-light transition-transform duration-500 group-hover:rotate-90">&times;</span>
          </button>
        </div>

        <div className="flex-1 p-8 space-y-10 overflow-y-auto bg-white no-scrollbar">
          {/* Section: Cover Illustration */}
          <section className="space-y-4">
            <h3 className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2">
               <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
               Image de couverture
            </h3>
            <label className="group relative block w-full aspect-video rounded-[2.5rem] overflow-hidden cursor-pointer bg-slate-50 border-2 border-dashed border-slate-100 hover:border-primary/40 transition-all duration-700">
              {imageFile || sendeData.image ? (
                <>
                  <img
                    src={imageFile ? URL.createObjectURL(imageFile) : sendeData.image}
                    alt="Preview"
                    className="object-cover w-full h-full transition-transform duration-1000 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-title/60 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-all duration-500 flex items-center justify-center">
                    <div className="flex flex-col items-center space-y-3 text-white transition-transform duration-500 transform translate-y-4 group-hover:translate-y-0">
                      <div className="flex items-center justify-center border shadow-2xl w-14 h-14 bg-white/20 backdrop-blur-md rounded-2xl border-white/20">
                         <AddAPhotoIcon className="text-xl" />
                      </div>
                      <span className="text-[10px] font-black uppercase tracking-[0.2em]">Remplacer l'image</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-300">
                  <div className="flex items-center justify-center w-20 h-20 mb-6 transition-all duration-700 bg-white shadow-xl rounded-3xl shadow-slate-200/50 group-hover:scale-110 group-hover:bg-primary group-hover:text-white">
                    <AddAPhotoIcon className="text-2xl" />
                  </div>
                  <span className="text-[11px] font-black uppercase tracking-[0.3em] text-slate-400">Importer un visuel</span>
                </div>
              )}
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            </label>
          </section>

          {/* Section: Main Details */}
          <section className="space-y-8">
             <div className="space-y-3">
                <label className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                  Informations de l'offre
                </label>
                <div className="grid grid-cols-1 gap-6">
                  <CustomInput
                    placeholder="Titre Principal"
                    name="title"
                    value={sendeData.title}
                    onChange={handleChange}
                    inputStyle="!h-16 !bg-slate-50 !border-slate-100 focus:!bg-white focus:!border-primary/20 !rounded-2xl !px-6 !font-black !text-title !text-base transition-all shadow-sm focus:shadow-xl focus:shadow-primary/5"
                  />
                  <CustomInput
                    placeholder="Sous-titre"
                    name="subTitle"
                    value={sendeData.subTitle}
                    onChange={handleChange}
                    inputStyle="!h-16 !bg-slate-50 !border-slate-100 focus:!bg-white focus:!border-primary/20 !rounded-2xl !px-6 !font-black !text-title !text-base transition-all shadow-sm focus:shadow-xl focus:shadow-primary/5"
                  />
                  <CustomInput
                    placeholder="Description pedagogique"
                    name="description"
                    value={sendeData.description}
                    onChange={handleChange}
                    inputStyle="!h-16 !bg-slate-50 !border-slate-100 focus:!bg-white focus:!border-primary/20 !rounded-2xl !px-6 !font-medium !text-title !text-base transition-all shadow-sm"
                  />
                </div>
             </div>

             {/* Section: Pricing Grid */}
             <div className="space-y-4">
                <label className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                  Grille Tarifaire (DT)
                </label>
                <div className="rounded-[2rem] border border-slate-100 bg-slate-50 p-3">
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => handlePricingModeChange("paid")}
                      className={`h-12 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] transition-all ${pricingMode === "paid" ? "bg-white text-title shadow-lg shadow-slate-200/60" : "text-text/50 hover:text-title"}`}
                    >
                      Offre Payante
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePricingModeChange("free")}
                      className={`h-12 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] transition-all ${pricingMode === "free" ? "bg-emerald-500 text-white shadow-lg shadow-emerald-200/60" : "text-text/50 hover:text-title"}`}
                    >
                      Offre Gratuite
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 bg-slate-50 p-6 rounded-[2rem] border border-slate-100">
                  <div className="space-y-2 text-center">
                    <span className="text-[9px] font-black text-text/40 uppercase tracking-widest">Mois</span>
                    <CustomInput inputType="number" value={sendeData.monthlyPrice} name="monthlyPrice" onChange={handleChange}
                      disabled={pricingMode === "free"}
                      inputStyle="!h-12 !bg-white !border-slate-100 !rounded-xl !font-black !text-center" />
                  </div>
                  <div className="space-y-2 text-center">
                    <span className="text-[9px] font-black text-text/40 uppercase tracking-widest">Trimestre</span>
                    <CustomInput inputType="number" value={sendeData.trimesterPrice} name="trimesterPrice" onChange={handleChange}
                      disabled={pricingMode === "free"}
                      inputStyle="!h-12 !bg-white !border-slate-100 !rounded-xl !font-black !text-center" />
                  </div>
                  <div className="space-y-2 text-center">
                    <span className="text-[9px] font-black text-text/40 uppercase tracking-widest">Semestre</span>
                    <CustomInput inputType="number" value={sendeData.semesterPrice} name="semesterPrice" onChange={handleChange}
                      disabled={pricingMode === "free"}
                      inputStyle="!h-12 !bg-white !border-slate-100 !rounded-xl !font-black !text-center" />
                  </div>
                  <div className="space-y-2 text-center">
                    <span className="text-[9px] font-black text-text/40 uppercase tracking-widest">Année</span>
                    <CustomInput inputType="number" value={sendeData.yearlyPrice} name="yearlyPrice" onChange={handleChange}
                      disabled={pricingMode === "free"}
                      inputStyle="!h-12 !bg-white !border-slate-100 !rounded-xl !font-black !text-center" />
                  </div>
                </div>
                {pricingMode === "free" && (
                  <div className="rounded-[2rem] border border-emerald-100 bg-emerald-50/70 p-6">
                    <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Durée de l'offre gratuite</p>
                    <p className="mt-1 text-[11px] font-medium text-emerald-800/70">Définissez la durée d'accès en jours.</p>
                    <div className="mt-4">
                      <CustomInput
                        placeholder="Nombre de jours"
                        inputType="number"
                        value={sendeData.freeDurationDays}
                        name="freeDurationDays"
                        onChange={handleChange}
                        inputStyle="!h-14 !bg-white !border-emerald-100 !rounded-2xl !px-6 !font-black"
                      />
                    </div>
                  </div>
                )}
             </div>

             {/* Target Scope */}
             <div className="space-y-4">
                <label className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                  Ciblage & Périmètre
                </label>
                <div className="space-y-6">
                  <CustomSelect
                    placeholder="Sélectionner Classe"
                    options={groupOptions}
                    value={sendeData.classId}
                    onChange={handleSelectChange}
                    name="classId"
                    selectStyle="!h-16 !bg-slate-50 !border-slate-100 focus:!bg-white !rounded-2xl !px-6 !font-black !text-title transition-all"
                  />
                  <CustomSelect
                    placeholder="Sélectionner Niveau"
                    options={classesLevel}
                    value={sendeData.requiredEducationLevel || initialEducationLevel?.value}
                    onChange={handleSelectChange}
                    name="requiredEducationLevel"
                    selectStyle="!h-16 !bg-slate-50 !border-slate-100 focus:!bg-white !rounded-2xl !px-6 !font-black !text-title transition-all"
                  />
                  
                  <div className="flex flex-col gap-4 bg-slate-50 p-6 rounded-[2rem] border border-slate-100">
                     <div className="flex items-center justify-between">
                        <span className="text-[11px] font-black text-title uppercase tracking-wider">Matières illimitées</span>
                        <div 
                          className={`w-12 h-6 rounded-full transition-all duration-300 cursor-pointer relative ${sendeData.allSubjects ? 'bg-primary' : 'bg-slate-200'}`}
                          onClick={() => setSendeData(p => ({...p, allSubjects: !p.allSubjects, subjectCount: !p.allSubjects ? "" : p.subjectCount}))}
                        >
                           <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all duration-300 ${sendeData.allSubjects ? 'left-7' : 'left-1'}`}></div>
                        </div>
                     </div>
                     {!sendeData.allSubjects && (
                       <div className="space-y-4">
                         <div className="space-y-3">
                           <div>
                             <p className="text-[10px] font-black text-title uppercase tracking-widest">Matières incluses automatiquement</p>
                             <div className="flex items-center justify-between gap-4 px-4 py-3 mt-2 bg-white border rounded-2xl border-slate-200">
                               <p className="text-[11px] font-medium text-text/60">
                                 {sendeData.autoAssignSubjects === false
                                   ? "Mode manuel: attribution des matières faite manuellement."
                                   : "Mode auto: les matières sélectionnées sont attribuées automatiquement après acceptation."}
                               </p>
                               <div
                                 className={`w-12 h-6 rounded-full transition-all duration-300 cursor-pointer relative ${sendeData.autoAssignSubjects === false ? 'bg-slate-200' : 'bg-primary'}`}
                                 onClick={() =>
                                   setSendeData((prevData) => ({
                                     ...prevData,
                                     autoAssignSubjects: !Boolean(prevData.autoAssignSubjects),
                                   }))
                                 }
                               >
                                 <div
                                   className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all duration-300 ${sendeData.autoAssignSubjects === false ? 'left-1' : 'left-7'}`}
                                 ></div>
                               </div>
                             </div>
                           </div>
                           {sendeData.autoAssignSubjects !== false && (
                             <>
                               <CustomInput
                                 placeholder="Nombre de matières autorisées"
                                 inputType="number"
                                 value={sendeData.subjectCount}
                                 name="subjectCount"
                                 onChange={handleChange}
                                 inputStyle="!h-14 !bg-white !border-slate-100 !rounded-2xl !px-6 !font-black"
                               />
                               {availableSubjects.length > 0 ? (
                                 <div className="flex flex-wrap gap-2">
                                   {availableSubjects.map((subject) => {
                                     const isSelected = (sendeData.subjectIds || []).includes(Number(subject.id));
                                     return (
                                       <button
                                         key={subject.id}
                                         type="button"
                                         onClick={() => toggleSubject(Number(subject.id))}
                                         className={`px-4 py-2 rounded-2xl text-[10px] font-black uppercase tracking-wide transition-all border ${isSelected ? 'bg-primary text-white border-primary shadow-lg shadow-primary/20' : 'bg-white text-text/50 border-slate-200 hover:border-primary/30 hover:text-primary'}`}
                                       >
                                         {subject.speciality}
                                       </button>
                                     );
                                   })}
                                 </div>
                               ) : (
                                 <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-5 text-[10px] font-black uppercase tracking-widest text-text/30">
                                   Sélectionnez d'abord une classe pour charger ses matières.
                                 </div>
                               )}
                             </>
                           )}
                         </div>
                       </div>
                     )}
                  </div>
                </div>
             </div>

             {/* Section: Benefits */}
             <div className="space-y-4">
                <label className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                  Avantages inclus
                </label>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <CustomInput
                      placeholder="E.g. Accès illimité aux cours"
                      value={benefitInput}
                      onChange={(e) => setBenefitInput(e.target.value)}
                      inputStyle="!h-16 !bg-slate-50 !border-slate-100 focus:!bg-white !rounded-2xl !px-6 !font-medium"
                    />
                  </div>
                  <button 
                    onClick={handleAddBenefit}
                    className="flex items-center justify-center flex-shrink-0 w-16 h-16 text-white shadow-xl bg-primary rounded-2xl shadow-primary/20"
                  >
                    <AddCircleIcon />
                  </button>
                </div>

                <div className="space-y-2">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center justify-between p-4 transition-all border bg-slate-50 border-slate-100 rounded-2xl hover:bg-white hover:border-primary/20 group">
                      <span className="text-[11px] font-bold text-title uppercase tracking-wider">{benefit}</span>
                      <button onClick={() => handleRemoveBenefit(index)} className="flex items-center justify-center w-8 h-8 transition-all text-slate-300 hover:text-rose-500">
                        <RemoveCircleIcon fontSize="small" />
                      </button>
                    </div>
                  ))}
                  {benefits.length === 0 && (
                    <div className="py-8 text-center border-2 border-dashed border-slate-50 rounded-[2rem]">
                      <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Aucun avantage ajouté</p>
                    </div>
                  )}
                </div>
             </div>
          </section>
        </div>

        {/* Footer Actions */}
        <div className="z-40 flex flex-shrink-0 gap-4 p-8 bg-white border-t border-gray-100">
           <button 
              onClick={() => { setSendeData(defaultData); setPricingMode("paid"); setImageFile(null); onClose(); }}
              className="px-8 h-14 rounded-2xl font-black text-[11px] uppercase tracking-widest text-text/40 hover:text-rose-500 hover:bg-rose-50 active:scale-95 transition-all duration-300 pointer-events-auto"
           >
              Annuler
           </button>
           <button 
              onClick={handleActionClick}
              className="flex-1 h-14 bg-primary text-white rounded-2xl font-black text-[11px] uppercase tracking-[0.3em] shadow-2xl shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-1 active:scale-95 transition-all duration-300 flex items-center justify-center gap-4"
           >
              {buttonText}
           </button>
        </div>
      </div>
    </Modal>
  );
};

export default OfferStudentModal;
